import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mic, MicOff, Loader2, Check, X, AlertCircle, Sparkles, ClipboardList, History, Edit3, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function AIScribePage() {
  const [searchParams] = useSearchParams();
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [selectedPatientName, setSelectedPatientName] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [soapDraft, setSoapDraft] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [viewingSession, setViewingSession] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [activeTab, setActiveTab] = useState('record');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    API.get('/patients').then(r => {
      setPatients(r.data);
      const preselectId = searchParams.get('patient_id');
      if (preselectId) {
        const p = r.data.find(p => p.id === preselectId);
        if (p) { setSelectedPatientId(p.id); setSelectedPatientName(p.name); }
      }
    }).catch(console.error);
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await API.get('/ai-scribe/sessions');
      setSessions(res.data);
    } catch (err) { console.error(err); }
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(blob);
      };
      mediaRecorder.start(1000);
      setIsRecording(true);
      toast.success('Recording started');
    } catch (err) { toast.error('Microphone access denied'); }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const transcribeAudio = async (blob) => {
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'recording.webm');
      const res = await API.post('/ai-scribe/transcribe', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTranscription(prev => prev ? prev + '\n' + res.data.transcription : res.data.transcription);
      toast.success('Transcription complete');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Transcription failed';
      toast.error(msg);
    } finally { setTranscribing(false); }
  };

  const generateSOAP = async () => {
    if (!transcription.trim()) { toast.error('No transcription to process'); return; }
    if (!selectedPatientId) { toast.error('Please select a patient first'); return; }
    setGenerating(true);
    try {
      const res = await API.post('/ai-scribe/generate-soap', {
        transcription,
        patient_id: selectedPatientId,
        patient_name: selectedPatientName,
      });
      setSoapDraft(res.data.soap_draft);
      setSessionId(res.data.session_id);
      setEditing(false);
      setEditDraft(null);
      if (res.data.warning) toast.warning(res.data.warning);
      else toast.success('SOAP notes generated');
      fetchSessions();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Generation failed';
      toast.error(msg);
    } finally { setGenerating(false); }
  };

  const startEditing = () => {
    const draft = soapDraft || viewingSession?.soap_draft;
    if (draft) {
      setEditDraft({ ...draft });
      setEditing(true);
    }
  };

  const saveEdits = async () => {
    const sid = sessionId || viewingSession?.id;
    if (!sid || !editDraft) return;
    try {
      await API.post('/ai-scribe/update-session', {
        session_id: sid,
        subjective: editDraft.subjective || '',
        objective: editDraft.objective || '',
        assessment: editDraft.assessment || '',
        plan: editDraft.plan || '',
        diagnosis_tags: editDraft.diagnosis_tags || [],
        medications_mentioned: editDraft.medications_mentioned || [],
      });
      toast.success('Changes saved');
      setEditing(false);
      if (soapDraft) setSoapDraft(editDraft);
      if (viewingSession) setViewingSession({ ...viewingSession, soap_draft: editDraft });
      fetchSessions();
    } catch (err) { toast.error('Save failed'); }
  };

  const approveDraft = async (approved) => {
    const sid = sessionId || viewingSession?.id;
    if (!sid) return;
    try {
      await API.post('/ai-scribe/approve', { session_id: sid, approved });
      toast.success(approved ? 'Draft approved and saved' : 'Draft rejected');
      fetchSessions();
      if (!approved) { setSoapDraft(null); setSessionId(null); setViewingSession(null); }
    } catch (err) { toast.error('Failed to update'); }
  };

  const loadSession = (session) => {
    setViewingSession(session);
    setSoapDraft(null);
    setSessionId(null);
    setEditing(false);
    setEditDraft(null);
    setActiveTab('record');
  };

  const currentDraft = editing ? editDraft : (soapDraft || viewingSession?.soap_draft);
  const currentSessionId = sessionId || viewingSession?.id;

  return (
    <div data-testid="ai-scribe-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{fontFamily:'Manrope'}}>AI Scribe</h1>
          <p className="text-sm text-slate-500">Record consultations and generate clinical documentation</p>
        </div>
      </div>

      {/* AI Warning */}
      <div className="p-3 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-100 dark:border-amber-900/20 flex gap-3">
        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-700 dark:text-amber-400">
          <strong>AI-Generated Draft</strong> - All notes require doctor review and explicit approval before saving to records.
        </p>
      </div>

      {/* Patient Selection */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardContent className="p-4">
          <Label className="mb-2 block font-semibold">Select Patient *</Label>
          <Select value={selectedPatientId} onValueChange={(v) => {
            setSelectedPatientId(v);
            const p = patients.find(p => p.id === v);
            setSelectedPatientName(p?.name || '');
          }}>
            <SelectTrigger className="h-12" data-testid="scribe-patient-select">
              <SelectValue placeholder="Choose a patient for this session..." />
            </SelectTrigger>
            <SelectContent>
              {patients.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name} {p.age ? `(${p.age}y)` : ''} {p.phone ? `- ${p.phone}` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-sm">
          <TabsTrigger value="record" data-testid="scribe-tab-record">
            <Mic className="w-4 h-4 mr-2" /> Record & Generate
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="scribe-tab-history">
            <History className="w-4 h-4 mr-2" /> Saved Notes ({sessions.length})
          </TabsTrigger>
        </TabsList>

        {/* Record Tab */}
        <TabsContent value="record">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Recording Panel */}
            <div className="space-y-4">
              <Card className="border-slate-200 dark:border-slate-800">
                <CardContent className="p-8 flex flex-col items-center">
                  <div className="relative mb-6">
                    {isRecording && (
                      <><div className="absolute inset-0 w-32 h-32 -m-4 rounded-full bg-red-500/20 animate-pulse-ring" />
                      <div className="absolute inset-0 w-32 h-32 -m-4 rounded-full bg-red-500/10 animate-pulse-ring" style={{animationDelay:'0.5s'}} /></>
                    )}
                    <button data-testid="scribe-mic-btn" onClick={isRecording ? stopRecording : startRecording} disabled={transcribing}
                      className={`relative z-10 w-24 h-24 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isRecording ? 'bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/30 scale-110' : 'bg-teal-700 hover:bg-teal-800 shadow-lg shadow-teal-700/20'
                      }`}>
                      {isRecording ? <MicOff className="w-8 h-8 text-white" /> : <Mic className="w-8 h-8 text-white" />}
                    </button>
                  </div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white mb-1">
                    {isRecording ? 'Recording... Tap to stop' : transcribing ? 'Transcribing...' : 'Tap to start recording'}
                  </p>
                  <p className="text-xs text-slate-400">{isRecording ? 'Speak clearly near the microphone' : 'Record doctor-patient consultation'}</p>
                  {transcribing && <div className="mt-4 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-teal-700" /><span className="text-sm text-teal-700">Transcribing...</span></div>}
                </CardContent>
              </Card>

              <Card className="border-slate-200 dark:border-slate-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Transcription</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea data-testid="scribe-transcription" value={transcription} onChange={e => setTranscription(e.target.value)}
                    placeholder="Transcription will appear here after recording... You can also paste or type text directly." className="min-h-[200px] text-sm" />
                  <Button className="mt-3 w-full h-11 bg-teal-700 hover:bg-teal-800 text-white" onClick={generateSOAP} disabled={!transcription.trim() || generating || !selectedPatientId} data-testid="scribe-generate-btn">
                    {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate SOAP Notes</>}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* SOAP Draft Panel */}
            <SOAPDraftPanel
              draft={currentDraft}
              editing={editing}
              editDraft={editDraft}
              setEditDraft={setEditDraft}
              onStartEdit={startEditing}
              onSaveEdits={saveEdits}
              onApprove={approveDraft}
              sessionId={currentSessionId}
              viewingSession={viewingSession}
            />
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Saved AI Scribe Sessions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2" data-testid="scribe-sessions-list">
              {sessions.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">No saved sessions yet</p>
                </div>
              ) : (
                sessions.map((s, i) => (
                  <div key={s.id} className="flex items-center justify-between p-4 bg-white dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-800 hover:shadow-sm transition-all cursor-pointer"
                    onClick={() => { loadSession(s); setActiveTab('record'); }} data-testid={`session-item-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm text-slate-900 dark:text-white truncate">{s.patient_name || 'Unknown Patient'}</p>
                        <Badge className={`text-[10px] ${
                          s.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                          s.status === 'rejected' ? 'bg-red-50 text-red-600' :
                          'bg-amber-100 text-amber-800'
                        }`}>{s.status}</Badge>
                      </div>
                      <p className="text-xs text-slate-400">{new Date(s.created_at).toLocaleDateString('en-IN')} {new Date(s.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
                      {s.soap_draft?.assessment && <p className="text-xs text-slate-500 mt-1 truncate">Assessment: {s.soap_draft.assessment}</p>}
                    </div>
                    <ClipboardList className="w-5 h-5 text-slate-300 ml-3 shrink-0" />
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SOAPDraftPanel({ draft, editing, editDraft, setEditDraft, onStartEdit, onSaveEdits, onApprove, sessionId, viewingSession }) {
  if (!draft && !viewingSession) {
    return (
      <Card className="border-slate-200 dark:border-slate-800 h-fit">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}>
            <ClipboardList className="w-5 h-5 text-teal-600" /> SOAP Draft
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-16">
            <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">SOAP notes will appear here after generation</p>
            <p className="text-slate-300 text-xs mt-1">Or select a saved session from History</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const displayDraft = editing ? editDraft : draft;

  return (
    <Card className="border-slate-200 dark:border-slate-800 h-fit">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}>
            <ClipboardList className="w-5 h-5 text-teal-600" /> SOAP Draft
          </CardTitle>
          <div className="flex items-center gap-2">
            {viewingSession && <Badge variant="secondary" className="text-xs">{viewingSession.patient_name}</Badge>}
            <Badge className="bg-amber-100 text-amber-800 text-[10px]">AI Draft</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent data-testid="scribe-soap-draft" className="space-y-4">
        {['subjective', 'objective', 'assessment', 'plan'].map(key => (
          <div key={key}>
            <p className="text-xs font-bold text-teal-700 dark:text-teal-400 uppercase tracking-wider mb-1">
              {key.charAt(0).toUpperCase()} - {key}
            </p>
            {editing ? (
              <Textarea
                data-testid={`edit-soap-${key}`}
                value={editDraft?.[key] || ''}
                onChange={e => setEditDraft({ ...editDraft, [key]: e.target.value })}
                className="min-h-[60px] text-sm"
              />
            ) : (
              <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                {displayDraft?.[key] || '-'}
              </p>
            )}
          </div>
        ))}

        {displayDraft?.diagnosis_tags?.length > 0 && (
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Diagnoses</p>
            <div className="flex flex-wrap gap-1.5">
              {displayDraft.diagnosis_tags.map(d => <Badge key={d} variant="secondary" className="text-xs">{d}</Badge>)}
            </div>
          </div>
        )}

        {displayDraft?.medications_mentioned?.length > 0 && (
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Medications</p>
            {displayDraft.medications_mentioned.map((m, i) => (
              <div key={`el-${i}`} className="text-sm text-slate-600 dark:text-slate-400">
                <strong>{m.name}</strong> {m.dosage} - {m.frequency} ({m.duration})
              </div>
            ))}
          </div>
        )}

        {displayDraft?.allergies_mentioned?.length > 0 && (
          <div>
            <p className="text-xs font-bold text-red-500 uppercase tracking-wider mb-1">Allergies Detected</p>
            <div className="flex flex-wrap gap-1.5">
              {displayDraft.allergies_mentioned.map(a => <Badge key={a} className="bg-red-50 text-red-600 border-red-100">{a}</Badge>)}
            </div>
          </div>
        )}

        <Separator />

        <div className="flex gap-2 flex-wrap">
          {editing ? (
            <>
              <Button className="flex-1 h-11 bg-teal-700 hover:bg-teal-800 text-white" onClick={onSaveEdits} data-testid="scribe-save-edit-btn">
                <Save className="w-4 h-4 mr-2" /> Save Changes
              </Button>
              <Button variant="outline" className="h-11" onClick={() => { setEditDraft(null); }} data-testid="scribe-cancel-edit-btn">
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="flex-1 h-11" onClick={onStartEdit} data-testid="scribe-edit-btn">
                <Edit3 className="w-4 h-4 mr-2" /> Edit Notes
              </Button>
              <Button className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onApprove(true)} data-testid="scribe-approve-btn">
                <Check className="w-4 h-4 mr-2" /> Approve
              </Button>
              <Button variant="outline" className="h-11 text-red-500 border-red-200 hover:bg-red-50" onClick={() => onApprove(false)} data-testid="scribe-reject-btn">
                <X className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
